<script setup>
import { Smartphone, Navigation, PackageCheck, ArrowRight, Building2 } from 'lucide-vue-next'

const steps = [
  {
    number: '01',
    icon: Smartphone,
    label: 'App / Panel',
    title: 'Cotizas y Pides',
    description: 'Desde la app móvil o el panel de tu negocio, ingresas el origen y el destino. Conoces la tarifa exacta por adelantado, sin cobros sorpresa al confirmar.'
  },
  {
    number: '02',
    icon: Navigation,
    label: 'Ruta / Mapa',
    title: 'Asignamos y Rastrean',
    description: 'El sistema conecta con el repartidor verificado más cercano. A partir de ahí, tú y tu cliente ven el avance de la moto en el mapa en tiempo real.'
  },
  {
    number: '03',
    icon: PackageCheck,
    label: 'Entrega / Historial',
    title: 'Entregamos y Registramos',
    description: 'Se completa el despacho con seguridad. Notificamos al cliente y el detalle de la entrega queda guardado en tu historial para que tengas control total.'
  }
]

</script>

<template>
  <section id="como-funciona" class="relative scroll-mt-24 overflow-hidden bg-[#f7f8fa] py-20 md:py-28">
    <div class="container relative z-10 mx-auto px-6 lg:px-12">
      <!-- Cabecera -->
      <div class="grid items-end gap-6 lg:grid-cols-12">
        <div v-reveal class="lg:col-span-7">
          <p class="text-[11px] font-black uppercase tracking-[0.18em] text-[#c2410c]">Cómo funciona</p>
          <h2 class="mt-4 text-balance text-3xl font-black tracking-[-0.03em] text-[#2b4162] sm:text-4xl md:text-5xl">
            De tu local (o tu casa)
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-[#f0611e] to-[#d85317]">a la puerta del destino, en tres pasos.</span>
          </h2>
        </div>
        <div v-reveal="{ delay: 120 }" class="lg:col-span-5">
          <p class="max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            La misma potencia logística para un negocio que despacha 50 paquetes diarios y para alguien que solo necesita enviar uno.
          </p>
        </div>
      </div>

      <!-- Línea de tiempo -->
      <div class="relative mt-14">
        <div class="absolute left-[10%] right-[10%] top-7 hidden h-px bg-gradient-to-r from-transparent via-[#f0611e]/40 to-transparent md:block"></div>

        <div class="grid gap-8 md:grid-cols-3">
          <div
            v-for="(step, index) in steps"
            :key="step.number"
            v-reveal="{ delay: index * 100 }"
            class="relative"
          >
            <div class="relative z-10 flex items-center justify-between">
              <span class="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0a1120] text-[#ff8a4c] shadow-[0_12px_30px_rgba(10,17,32,0.18)]">
                <component :is="step.icon" :size="22" />
              </span>
              <span class="font-mono text-5xl font-black leading-none tracking-tighter text-slate-300">
                {{ step.number }}
              </span>
            </div>

            <div class="mt-6 rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#f0611e]/30 hover:shadow-panel">
              <span class="inline-flex rounded-full bg-[#fff1ea] px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-[#c2410c]">
                {{ step.label }}
              </span>
              <h3 class="mt-4 text-xl font-black tracking-tight text-[#2b4162] sm:text-2xl">{{ step.title }}</h3>
              <p class="mt-2 text-sm leading-relaxed text-slate-600">{{ step.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA de sección -->
      <div v-reveal class="mt-14 flex flex-col gap-4 border-t border-slate-200 pt-8 sm:flex-row sm:items-center sm:justify-between">
        <p class="text-sm font-semibold text-[#2b4162]">¿Listo para tu primer despacho con LLEVO?</p>
        <div class="flex flex-col gap-3 sm:flex-row">
          <router-link
            to="/postular-negocio"
            class="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#2b4162] px-5 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1c2b42]"
          >
            <Building2 :size="16" />
            Registrar mi negocio
          </router-link>
          <a
            href="#app-usuarios"
            class="group inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-[#2b4162] transition-all hover:-translate-y-0.5 hover:border-[#c2410c]/40 hover:text-[#c2410c]"
          >
            Descargar la app
            <ArrowRight :size="15" class="transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </div>
  </section>
</template>
